<template>
  <div class="gpi-form flex flex-col gap-4">
    <PaymentFormInput
      v-model="name"
      label="Cardholder's name"
      :error-message="firstErrorMessage(v$.name)"
      data-cy="name"
    />

    <div class="grid grid-cols-1 md:grid-cols-[1fr_140px_120px] gap-3">
      <div class="flex flex-col gap-1.5">
        <span
          class="block text-sm font-medium text-gray-900"
        >
          Card number
        </span>
        <div id="card-number" class="gpi-field"></div>
      </div>
      <div class="flex flex-col gap-1.5">
        <span
          class="block text-sm font-medium text-gray-900"
        >
          Expiration
        </span>
        <div id="card-expiration" class="gpi-field"></div>
      </div>
      <div class="flex flex-col gap-1.5">
        <span
          class="block text-sm font-medium text-gray-900"
        >
          CVV
        </span>
        <div id="card-cvv" class="gpi-field"></div>
      </div>
    </div>

    <BillingAddressForm ref="billingAddressForm" :address="billingAddress" />

    <div id="submit" class="hidden"></div>

    <SaveForFuturePayments
      v-if="canSaveCard"
      v-model="saveCard"
      data-cy="gpi-save-card"
    />
  </div>
</template>

<script>
import { nextTick } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import SaveForFuturePayments from "@/components/PaymentModals/SaveForFuturePayments.vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import BillingAddressForm from "./BillingAddressForm.vue";
import { loadGlobalPayments } from "./paymentSdks";
import usePaymentMethods from "./usePaymentMethods";
export default {
  components: {
    BillingAddressForm,
    PaymentFormInput,
    SaveForFuturePayments
  },
  props: {
    autoSaveCard: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  setup() {
    const { billingAddress, paymentMethodsResponse } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      billingAddress,
      v$: useVuelidate(),
      firstErrorMessage
    };
  },
  validations() {
    return {
      name: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      form: null,
      name: "",
      resolver: null,
      rejector: null,
      saveCard: false
    };
  },
  computed: {
    config() {
      return this.paymentMethodsResponse.find(
        (method) => method.key === "gpi_card"
      );
    },
    canSaveCard() {
      return !!this.config?.card_savable && !this.autoSaveCard;
    }
  },
  async mounted() {
    try {
      await loadGlobalPayments();
      if (this.sdkLoadAborted) return;
      await nextTick();
      if (this.sdkLoadAborted) return;
      this.initForm();
      this.attachSuccessHandler();
      this.attachErrorHandler();
    } catch (error) {
      window.notify.error(
        "Unable to load the payment form. Please refresh and try again."
      );
      window.Sentry?.captureException(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  },
  beforeUnmount() {
    // The SDK load is async; if the form unmounts first, skip initForm so the
    // SDK can't bind its hosted-field iframe onto another form's shared DOM ids.
    this.sdkLoadAborted = true;
  },
  methods: {
    attachErrorHandler() {
      this.form.on("token-error", (resp) => {
        window.notify.error(resp.error.message);
        this.rejector(resp.error.message);
      });
    },
    attachSuccessHandler() {
      this.form.on("token-success", async (resp) => {
        this.resolver({
          ...(await this.$refs.billingAddressForm.getData()),
          name: this.name,
          card_token: resp.temporary_token,
          card_save_payment_method: this.saveCard || this.autoSaveCard
        });
      });
    },
    async getData() {
      await this.$refs.billingAddressForm.getData();
      const validated = await this.v$.$validate();
      return await new Promise((resolve, reject) => {
        this.resolver = resolve;
        this.rejector = reject;
        if (validated) {
          try {
            this.form.frames.submit.events.click[0]();
          } catch (e) {
            throw new Error(
              "Unable to submit GPI Payment form with error: " + e
            );
          }
        } else {
          reject();
        }
      });
    },
    initForm() {
      if (!this.config) {
        return;
      }
      GlobalPayments.configure({
        "X-GP-Api-Key": this.config.tokenization_key,
        "X-GP-Environment": this.config.environment
      });
      this.form = GlobalPayments.ui.form({
        fields: {
          "card-number": {
            target: "#card-number",
            placeholder: "•••• •••• •••• ••••"
          },
          "card-expiration": {
            target: "#card-expiration",
            placeholder: "MM / YYYY"
          },
          "card-cvv": {
            target: "#card-cvv",
            placeholder: "•••"
          },
          submit: {
            text: "Validate Card",
            target: "#submit"
          }
        },
        // Styles applied inside the GP-hosted iframe to mirror the
        // PaymentFormInput chrome (rounded-md, gray-300 border, blue-500
        // focus ring, gray-900 text). Color values come from Tailwind so the
        // iframe matches every other field in the payment modal.
        styles: {
          "button, input": {
            outline: "none",
            "border-width": "0"
          },
          "input, select": {
            display: "block",
            width: "100%",
            padding: "8px 12px",
            "line-height": "1.5",
            "font-size": "14px",
            color: "#111827",
            "background-color": "#fff",
            "background-clip": "padding-box",
            border: "1px solid #d1d5db",
            "border-radius": "6px",
            "box-sizing": "border-box"
          },
          "input::placeholder": {
            color: "#9ca3af"
          },
          "input:focus": {
            "border-color": "#3b82f6",
            "box-shadow": "0 0 0 1px #3b82f6"
          },
          button: {
            display: "none"
          },
          "input.invalid": {
            "border-color": "#f87171",
            "background-color": "#fff"
          },
          ".card-number.card-type-visa": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-visa@2x.png) no-repeat right",
            "background-position-y": "1px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-mastercard": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-mastercard@2x.png) no-repeat right",
            "background-position-y": "1px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-discover": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-discover@2x.png) no-repeat right",
            "background-position-y": "2px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-jcb": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-jcb@2x.png) no-repeat right",
            "background-position-y": "2px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-amex": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-amex@2x.png) no-repeat right",
            "background-position-y": "4px",
            "background-size": "60px 64px"
          }
        }
      });

      if (this.customer) {
        this.name =
          this.customer.full_name ||
          `${this.customer.first_name} ${this.customer.last_name}`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.gpi-form {
  :deep(iframe) {
    width: 100%;
    min-height: 38px;
  }
  // The host div for each GP-hosted iframe. Sized to match
  // PaymentFormInput's rendered height; the iframe itself paints the
  // border/focus state via the styles config in initForm().
  .gpi-field {
    height: 38px;
    background-color: #fff;
    border-radius: 6px;
  }
}
</style>
